/**
 * Panel 8: AAR Engine — Case Studies + Refueling Simulation
 */

import { useState, useEffect, useCallback } from 'react'
import type { AARCase, AARScorecard } from '../../../types'
import { getAARCases, getAARScorecard } from '../../../services/api'
import { TEMPO_CONFIG, type Tempo } from '../../../types'
import { RefuelingSimView } from './refueling-sim/RefuelingSimView'

type AARMode = 'cases' | 'refueling-sim'

export function AAREnginePanel() {
  const [mode, setMode] = useState<AARMode>('cases')
  const [cases, setCases] = useState<AARCase[]>([])
  const [scorecard, setScorecard] = useState<AARScorecard | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedCase, setSelectedCase] = useState<string | null>(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    const [casesRes, scorecardRes] = await Promise.all([getAARCases(), getAARScorecard()])
    if (casesRes.data) setCases(casesRes.data)
    if (scorecardRes.data) setScorecard(scorecardRes.data)
    setLoading(false)
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  return (
    <div className="flex flex-col gap-4 h-full">
      {/* Mode Toggle */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setMode('cases')}
          className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded border transition-colors
            ${mode === 'cases'
              ? 'bg-term-primary/20 border-term-primary text-term-primary'
              : 'bg-term-bg border-term-border text-term-textDim hover:border-term-primary/50 hover:text-term-text'}`}
        >
          Case Studies
        </button>
        <button
          onClick={() => setMode('refueling-sim')}
          className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded border transition-colors
            ${mode === 'refueling-sim'
              ? 'bg-term-primary/20 border-term-primary text-term-primary'
              : 'bg-term-bg border-term-border text-term-textDim hover:border-term-primary/50 hover:text-term-text'}`}
        >
          Refueling Sim
        </button>
      </div>

      {mode === 'refueling-sim' ? (
        <RefuelingSimView />
      ) : (
        <CaseStudiesView loading={loading} cases={cases} scorecard={scorecard} selectedCase={selectedCase} setSelectedCase={setSelectedCase} />
      )}
    </div>
  )
}

function CaseStudiesView({ loading, cases, scorecard, selectedCase, setSelectedCase }: {
  loading: boolean
  cases: AARCase[]
  scorecard: AARScorecard | null
  selectedCase: string | null
  setSelectedCase: (id: string | null) => void
}) {
  if (loading) {
    return <div className="animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-20 bg-term-bgLight rounded" />)}</div>
  }

  const selected = cases.find(c => c.id === selectedCase)

  return (
    <>
      {/* Scorecard */}
      {scorecard && (
        <div className="grid grid-cols-5 gap-3">
          <ScoreCard label="Total Cases" value={String(scorecard.total_cases)} />
          <ScoreCard label="Correct" value={String(scorecard.correct_classifications)} highlight />
          <ScoreCard label="Accuracy" value={`${(scorecard.accuracy * 100).toFixed(0)}%`} highlight />
          <ScoreCard label="Avg Lead Time" value={`${scorecard.avg_lead_time_hours}h`} />
          <ScoreCard label="FP / FN Rate" value={`${(scorecard.false_positive_rate * 100).toFixed(0)}% / ${(scorecard.false_negative_rate * 100).toFixed(0)}%`} />
        </div>
      )}

      {/* Accuracy by Tempo */}
      {scorecard && (
        <div className="term-box p-3">
          <div className="text-xs text-term-textDim font-medium uppercase tracking-wider mb-2">Accuracy by Tempo</div>
          <div className="flex gap-3">
            {Object.entries(scorecard.by_tempo).filter(([, data]) => data.cases > 0).map(([tempo, data]) => {
              const cfg = TEMPO_CONFIG[tempo as Tempo]
              const pct = Math.round(data.accuracy * 100)
              return (
                <div key={tempo} className="flex-1 text-center">
                  <div className={`${cfg?.cssClass ?? 'tempo-steady'} px-2 py-0.5 rounded text-[10px] font-medium mb-1`}>
                    {tempo}
                  </div>
                  <div className="text-lg font-bold text-term-text tabular-nums">{pct}%</div>
                  <div className="text-[10px] text-term-textDim">{data.cases} cases</div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Case List */}
      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
        <div className="w-1/3 flex flex-col gap-2 overflow-auto">
          {cases.map(c => {
            const isSelected = selectedCase === c.id
            const accColor = c.accuracy_pct >= 90 ? '#22c55e' : c.accuracy_pct >= 80 ? '#f59e0b' : '#ef4444'
            return (
              <div
                key={c.id}
                className={`term-box p-3 cursor-pointer transition-all ${isSelected ? 'ring-1 ring-term-primary' : 'hover:bg-term-bgLight/50'}`}
                onClick={() => setSelectedCase(isSelected ? null : c.id)}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-term-textDim text-xs font-mono">{c.id}</span>
                  <span className="text-xs font-bold tabular-nums" style={{ color: accColor }}>{c.accuracy_pct}%</span>
                </div>
                <div className="text-sm font-bold text-term-text">{c.title}</div>
                <div className="text-[10px] text-term-textDim mt-1">{c.scenario} | {c.date}</div>
              </div>
            )
          })}
        </div>

        {/* Detail */}
        <div className="w-2/3 overflow-auto">
          {selected ? (
            <div className="term-box p-4 h-full">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-lg font-bold text-term-text">{selected.title}</div>
                  <div className="text-xs text-term-textDim">{selected.scenario} | {selected.date}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-term-primary tabular-nums">{selected.accuracy_pct}%</div>
                  <div className="text-[10px] text-term-textDim">Accuracy</div>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-xs text-term-textDim uppercase mb-1">SENTINEL Classification</div>
                  <div className="text-sm text-term-text bg-term-bgLight p-2 rounded">{selected.sentinel_classification}</div>
                </div>
                <div>
                  <div className="text-xs text-term-textDim uppercase mb-1">Actual Outcome</div>
                  <div className="text-sm text-term-text bg-term-bgLight p-2 rounded">{selected.actual_outcome}</div>
                </div>
                <div>
                  <div className="text-xs text-term-textDim uppercase mb-1">Key Findings</div>
                  <ul className="space-y-1">
                    {selected.key_findings.map((f, i) => (
                      <li key={i} className="text-xs text-term-text flex gap-2">
                        <span className="text-term-primary shrink-0">*</span> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-xs text-term-textDim uppercase mb-1">Lessons Learned</div>
                  <div className="text-xs text-term-text leading-relaxed">{selected.lessons_learned}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-term-textDim text-sm">
              Select a case study to view details
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function ScoreCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`term-box p-3 text-center ${highlight ? 'border-term-success/30 glow-success' : ''}`}>
      <div className="text-[10px] text-term-textDim uppercase tracking-wider">{label}</div>
      <div className="text-lg font-bold text-term-text tabular-nums">{value}</div>
    </div>
  )
}
